import React, { useMemo } from 'react';

import Modal from '../../components/Modal';
import ActionButtons from '../../components/ActionButtons';
import clsx from 'clsx';
import { errorStyles, inputStyles } from '../../styles/styles';


const UploadModal = ({ materialTypes, isLoading, isOpen, form, handleSubmit, handleChange, handleFileChange, onClose, isEdit = false, selectedItem, setIsEdit, onSubmit, status, errors }) => {
    const labels = useMemo(
        () => ({
            normalLabel: isEdit ? 'Update Upload' : 'Create Upload',
            loadingLabel: isEdit ? 'Updating...' : 'Creating...'
        }),
        [isEdit]
    );

    // Determine if current material type is audio/video
    const selectedMaterialType = materialTypes?.find(type => {
        return type.id === Number(form?.material_type_id);
    });
    const isAudioOrVideo = selectedMaterialType?.name?.toLowerCase().includes('audio') || 
                          selectedMaterialType?.name?.toLowerCase().includes('video');

    return (
        <Modal isOpen={isOpen} onClose={onClose} title={isEdit ? 'UPDATE UPLOAD' : 'CREATE NEW UPLOAD'}>
            <form onSubmit={handleSubmit} className="space-y-4 ">
                {/* Title field */}
                <div>
                    <label htmlFor="title" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Title
                    </label>
                    <textarea
                        id="title"
                        autoComplete="off"
                        type="text"
                        placeholder="Enter title"
                        name="title"
                        value={form?.title || ''}
                        onChange={(e) => handleChange('title', e.target.value)}
                        className={clsx(inputStyles, errors?.title && 'border-red-500 dark:border-red-400')}
                    />
                    {errors?.title && <p className={errorStyles}>{errors?.title}</p>}
                </div>

                {/* Date field */}
                <div>
                    <label htmlFor="date" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Date
                    </label>
                    <input
                        id="date"
                        autoComplete="off"
                        type="date"
                        placeholder="Select date"
                        name="date"
                        value={form?.date || ''}
                        onChange={(e) => handleChange('date', e.target.value)}
                        className={clsx(inputStyles, errors?.date && 'border-red-500 dark:border-red-400')}
                    />
                    {errors?.date && <p className={errorStyles}>{errors?.date}</p>}
                </div>

                {/* Material Type field */}
                <div>
                    <label htmlFor="material_type_id" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Material Type
                    </label>
                    <select
                        id="material_type_id"
                        autoComplete="off"
                        placeholder="Select Material Type"
                        name="material_type_id"
                        value={form?.material_type_id || ''}
                        onChange={(e) => handleChange('material_type_id', e.target.value)}
                        className={clsx(inputStyles, errors?.material_type_id && 'border-red-500 dark:border-red-400')}
                    >
                        <option value="">Select Material Type</option>
                        {materialTypes?.map((item) => (
                            <option key={item.id} value={item.id}>
                                {item.name}
                            </option>
                        ))}
                    </select>
                    {errors?.material_type_id && <p className={errorStyles}>{errors?.material_type_id}</p>}
                </div>

                {/* Description field */}
                <div>
                    <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Description
                    </label>
                    <textarea
                        id="description"
                        autoComplete="off"
                        placeholder="Enter Description"
                        name="description"
                        value={form?.description || ''}
                        onChange={(e) => handleChange('description', e.target.value)}
                        className={clsx(inputStyles, errors?.description && 'border-red-500 dark:border-red-400')}
                    />
                    {errors?.description && <p className={errorStyles}>{errors?.description}</p>}
                </div>

                {/* Conditional fields based on material type */}
                {!isAudioOrVideo && (
                    <>
                        {/* Manual field - only for non-audio/video types */}
                        <div>
                            <label htmlFor="manual" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Manual
                            </label>
                            <input
                                id="manual"
                                autoComplete="off"
                                type="file"
                                placeholder="Upload Manual"
                                name="manual"
                                accept=".pdf,.doc,.docx"
                                onChange={(e) => {
                                    handleFileChange('manual', e.target.files[0]);
                                    handleChange('manual', e.target.value);
                                }}
                                className={clsx(inputStyles, errors?.manual && 'border-red-500 dark:border-red-400')}
                            />
                            <p className="text-xs text-gray-500 mt-1">Accepted formats: PDF, DOC, DOCX (Max size: 10MB)</p>
                            {errors?.manual && <p className={errorStyles}>{errors?.manual}</p>}
                        </div>

                        {/* Thumbnail field - only for non-audio/video types */}
                        <div>
                            <label htmlFor="thumbnail" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Thumbnail
                            </label>
                            <input
                                id="thumbnail"
                                autoComplete="off"
                                type="file"
                                placeholder="Upload Thumbnail"
                                name="thumbnail"
                                accept=".jpg,.jpeg,.png"
                                onChange={(e) => {
                                    handleFileChange('thumbnail', e.target.files[0]);
                                    handleChange('thumbnail', e.target.value);
                                }}
                                className={clsx(inputStyles, errors?.thumbnail && 'border-red-500 dark:border-red-400')}
                            />
                            <p className="text-xs text-gray-500 mt-1">Accepted formats: JPG, PNG, JPEG (Max size: 5MB)</p>
                            {errors?.thumbnail && <p className={errorStyles}>{errors?.thumbnail}</p>}
                        </div>
                    </>
                )}

                {/* URL field - only for audio/video types */}
                {isAudioOrVideo && (
                    <div>
                        <label htmlFor="url" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            URL
                        </label>
                        <input
                            id="url"
                            autoComplete="off"
                            type="text"
                            placeholder="Enter URL"
                            name="url"
                            value={form?.url || ''}
                            onChange={(e) => handleChange('url', e.target.value)}
                            className={clsx(inputStyles, errors?.url && 'border-red-500 dark:border-red-400')}
                        />
                        <p className="text-xs text-gray-500 mt-1">Enter the URL for your audio/video content</p>
                        {errors?.url && <p className={errorStyles}>{errors?.url}</p>}
                    </div>
                )}

                <ActionButtons isLoading={isLoading} onClose={onClose} onSubmit={handleSubmit} isEdit={isEdit} label={labels} />
            </form>
        </Modal>
    );
};

export default React.memo(UploadModal);
